import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Wand2, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

import { StudioBoard } from '../studio-contract';
import { cameraLabel, moveLabel } from '../labels';
import { ReferenceStatus } from '../use-reference';
import { LightboxMedia, MediaLightbox } from '../../../../components/design-system/common/MediaLightbox';

interface ReferenceStripProps {
    reference: NonNullable<StudioBoard['reference']>;
    status: ReferenceStatus;
    error: string | null;
    busy: boolean;
    onRemove: () => void;
    onRecreate: () => void;
    /** The file as it is being read, shown before the upload is done. */
    previewUrl?: string;
}

/**
 * The reference, read: what it is, and its shots one by one — so the vendor
 * sees what Ardi saw before asking it to recreate anything. Sits above the
 * board once the reading is done (the reading itself is ReferenceScan).
 */
export const ReferenceStrip: React.FC<ReferenceStripProps> = ({ reference, status, error, busy, onRemove, onRecreate, previewUrl }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(true);
    const [previewing, setPreviewing] = useState<LightboxMedia | null>(null);
    const title = t('STUDIO.REF.TITLE', 'Your reference');
    const a = reference.analysis;
    const reading = status === 'probing' || status === 'uploading' || status === 'reading';
    const url = previewUrl || reference.url;
    const ratio = reference.kind === 'video' ? '9 / 16' : '4 / 5';

    return (
        <div className="overflow-hidden rounded-2xl border bg-background animate-in fade-in slide-in-from-top-2 duration-500">
            <div className="flex flex-wrap items-center gap-3 p-3">
                <button type="button" onClick={() => setPreviewing({ url, kind: reference.kind === 'video' ? 'video' : 'image', title })}
                    title={t('STUDIO.OPEN_PREVIEW', 'Preview')} className="shrink-0 cursor-pointer overflow-hidden rounded-lg border transition-opacity hover:opacity-85">
                    {reference.kind === 'video'
                        ? <video src={url} className="h-14 w-10 bg-black object-cover" muted loop autoPlay playsInline />
                        : <img src={url} alt="" className="h-14 w-11 object-cover" />}
                </button>
                <div className="min-w-0 flex-1 basis-[200px]">
                    <div className="flex flex-wrap items-baseline gap-x-1.5 text-sm font-medium text-foreground">
                        {title}
                        {a && <span className="text-xs font-normal text-muted-foreground">{t('STUDIO.REF.SHOTS', '{{n}} shots', { n: a.shots.length })}{a.pacing ? ` · ${a.pacing}` : ''}</span>}
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-muted-foreground">
                        {reading ? (status === 'reading' ? t('STUDIO.REF.READING', 'Ardi is watching it — shots, camera, text, music…') : t('STUDIO.REF.PREPARING', 'Finding the cuts…'))
                            : error ? error
                                : a?.summary || ''}
                    </p>
                </div>
                <div className="ms-auto flex items-center gap-1">
                    {reading && <Spinner className="me-1 size-4" />}
                    {a && (
                        <Button variant="outline" size="sm" className="gap-1.5" disabled={busy} onClick={onRecreate}>
                            <Wand2 className="size-3.5" /><span className="hidden sm:inline">{t('STUDIO.REF.RECREATE', 'Recreate it with my products')}</span><span className="sm:hidden">{t('STUDIO.REF.RECREATE_SHORT', 'Recreate')}</span>
                        </Button>
                    )}
                    {a && (
                        <Button variant="ghost" size="icon-sm" onClick={() => setOpen((o) => !o)} aria-label={t('STUDIO.REF.TOGGLE', 'Show shots')}>
                            <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
                        </Button>
                    )}
                    <Button variant="ghost" size="icon-sm" onClick={onRemove} aria-label={t('STUDIO.REMOVE', 'Remove')}><X className="size-4" /></Button>
                </div>
            </div>
            {a && open && (
                <div className="flex gap-3 overflow-x-auto border-t bg-muted/20 p-3 [scrollbar-width:thin]">
                    {a.shots.map((s, i) => (
                        <div key={i} className="w-[124px] shrink-0 space-y-1.5 animate-in fade-in slide-in-from-bottom-1 fill-mode-both duration-500" style={{ animationDelay: `${i * 70}ms` }}>
                            <button type="button"
                                onClick={() => setPreviewing(reference.kind === 'video'
                                    ? { url: reference.url, kind: 'video', startAt: s.start, title }
                                    : { url: s.frameUrl || reference.url, kind: 'image', title })}
                                title={t('STUDIO.OPEN_PREVIEW', 'Preview')}
                                className="relative block w-full cursor-pointer overflow-hidden rounded-lg border bg-muted transition-opacity hover:opacity-90" style={{ aspectRatio: ratio }}>
                                {s.frameUrl && <img src={s.frameUrl} alt="" className="absolute inset-0 size-full object-cover" />}
                                <span className="absolute start-1.5 top-1.5 inline-flex h-5 items-center rounded-full bg-black/45 px-1.5 text-[10px] font-medium text-white backdrop-blur-md">{i + 1} · {(s.end - s.start).toFixed(1)}s</span>
                                {s.text?.content && <span className="absolute inset-x-1.5 bottom-1.5 truncate rounded-md bg-black/45 px-1.5 py-0.5 text-[10px] text-white backdrop-blur-md">“{s.text.content}”</span>}
                            </button>
                            <div className="text-[11px] font-medium leading-tight text-foreground">{cameraLabel(t, s.camera).label} · {moveLabel(t, s.motion).label}</div>
                            <div className="line-clamp-3 text-[11px] leading-snug text-muted-foreground">{s.action || s.description}</div>
                        </div>
                    ))}
                </div>
            )}
            <MediaLightbox media={previewing} onClose={() => setPreviewing(null)} />
        </div>
    );
};
